import React from "react";
import { useShallow } from "zustand/react/shallow";
import { motion } from "framer-motion";
import { Event } from "@uno/protocol";
import { useTranslation } from "react-i18next";
import { AppContext } from "context";
import useStoreRoom from "store/useStoreRoom";
import useClick from "hook/useClick";
import useHaptic from "hook/useHaptic";
import useDrawHaptic from "hook/useDrawHaptic";

const SkipTurn = React.memo(() => {
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const room = useStoreRoom(useShallow(state => ({ id: state.room?.id, quantityCardsToDraw: state.room?.gameState?.quantityCardsToDraw || 0 })));
    const hasCardsToDraw = room.quantityCardsToDraw > 0;
    const skipHaptic = useHaptic("light");
    const drawHaptic = useDrawHaptic(room.quantityCardsToDraw);
    const haptic = hasCardsToDraw ? drawHaptic : skipHaptic;
    const ref = React.useRef<HTMLButtonElement>(null);
    const handleClick = React.useCallback(() => socket?.emit(Event.RoomPlayerSkipTurn, room?.id), [socket, room?.id]);

    useClick(handleClick, ref, { onHaptic: haptic });

    return (
        <motion.button
            ref={ref}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
                duration: 0.25,
                ease: "easeIn"
            }}
            className="room-action-tooltip absolute z-20 w-fit px-3 py-1.5 xs:px-4 xs:py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-lg text-xs xs:text-sm font-medium after:content-[''] after:absolute after:left-1/2 after:border-[6px] xs:after:border-8 after:border-transparent whitespace-nowrap cursor-pointer"
        >
            {hasCardsToDraw ? t("room.draw_cards", { count: room.quantityCardsToDraw }) : t("room.skip_turn")}
            <motion.div
                className="absolute inset-0 rounded-lg"
                animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.8, 0.3, 0],
                }}
                transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                style={{
                    backgroundColor: "white",
                    zIndex: -1,
                    boxShadow: "0 0 15px rgba(255,255,255,0.5)"
                }}
            />
        </motion.button>
    );
});

export default SkipTurn;
